import { supabase } from './supabase';
import { sendSMS } from './messaging';
import { 
  AutomationV2, 
  AutomationNode,
  AutomationConnection,
  AutomationExecution,
  AutomationExecutionStep,
  TriggerNode,
  ActionNode,
  WireConnection,
  CreateAutomationRequest,
  CreateNodeRequest,
  CreateConnectionRequest,
  BookingTriggerConfig,
  SMSActionConfig
} from '../types/automationsV2';

// ==================== HELPER FUNCTIONS ====================

// Convert database AutomationNode to frontend TriggerNode
function convertNodeToTrigger(node: AutomationNode): TriggerNode {
  return {
    id: node.id,
    type: node.node_subtype, // 'booking', etc.
    name: node.name,
    position: { x: node.position_x, y: node.position_y },
    config: node.configuration
  };
}

// Convert database AutomationNode to frontend ActionNode
function convertNodeToAction(node: AutomationNode): ActionNode {
  return {
    id: node.id,
    type: node.node_subtype, // 'sms', 'email', etc.
    name: node.name,
    position: { x: node.position_x, y: node.position_y },
    config: node.configuration
  };
}

// Convert database AutomationConnection to frontend WireConnection
function convertConnectionToWire(connection: AutomationConnection): WireConnection {
  return {
    id: connection.id,
    fromNodeId: connection.source_node_id,
    toNodeId: connection.target_node_id,
    fromPort: 'outflow',
    toPort: 'inflow'
  };
}

// Convert frontend TriggerNode to database format
function convertTriggerToNode(trigger: TriggerNode, automationId: string): Partial<AutomationNode> {
  return {
    automation_id: automationId,
    node_type: 'trigger',
    node_subtype: trigger.type, // 'booking', etc.
    name: trigger.name,
    position_x: trigger.position.x,
    position_y: trigger.position.y,
    configuration: trigger.config
  };
}

// Convert frontend ActionNode to database format
function convertActionToNode(action: ActionNode, automationId: string): Partial<AutomationNode> {
  return {
    automation_id: automationId,
    node_type: 'action',
    node_subtype: action.type, // 'sms', 'email', etc.
    name: action.name,
    position_x: action.position.x,
    position_y: action.position.y,
    configuration: action.config
  };
}

// ==================== AUTOMATION CRUD ====================

// Get current business ID (you may need to adapt this to your auth system)
export async function getCurrentBusinessId(): Promise<string | null> {
  if (!supabase) return null;
  
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('business_users')
      .select('business_id')
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    return data?.business_id || null;
  } catch (error) {
    console.error('Error getting current business ID:', error);
    return null;
  }
}

// Get all automations for a business
export async function getAutomations(businessId: string): Promise<AutomationV2[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching automations:', error);
    throw error;
  }
}

// Create a new automation
export async function createAutomation(request: CreateAutomationRequest): Promise<AutomationV2> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .insert({
        business_id: request.business_id,
        name: request.name,
        description: request.description,
        is_live: false
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation:', error);
    throw error;
  }
}

// Toggle automation live status
export async function toggleAutomationLive(automationId: string, isLive: boolean): Promise<AutomationV2> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .update({ is_live: isLive })
      .eq('id', automationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error toggling automation live status:', error);
    throw error;
  }
}

// Update automation details
export async function updateAutomation(automationId: string, updates: { name?: string }): Promise<AutomationV2> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .update(updates)
      .eq('id', automationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating automation:', error);
    throw error;
  }
}

// Delete automation and all related data
export async function deleteAutomation(automationId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // Delete all related data in the correct order
    
    // Delete automation execution steps first (get execution IDs first)
    const { data: executions } = await supabase
      .from('automation_executions')
      .select('id')
      .eq('automation_id', automationId);

    if (executions && executions.length > 0) {
      const executionIds = executions.map(e => e.id);
      const { error: stepsError } = await supabase
        .from('automation_execution_steps')
        .delete()
        .in('execution_id', executionIds);

      if (stepsError && stepsError.code !== 'PGRST116') {
        throw new Error(`Failed to delete automation execution steps: ${stepsError.message}`);
      }
    }

    // Delete automation executions
    const { error: executionsError } = await supabase
      .from('automation_executions')
      .delete()
      .eq('automation_id', automationId);

    if (executionsError && executionsError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation executions: ${executionsError.message}`);
    }

    // Delete automation jobs
    const { error: jobsError } = await supabase
      .from('automation_jobs')
      .delete()
      .eq('automation_id', automationId);

    if (jobsError && jobsError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation jobs: ${jobsError.message}`);
    }

    // Delete automation connections
    const { error: connectionsError } = await supabase
      .from('automation_connections')
      .delete()
      .eq('automation_id', automationId);

    if (connectionsError && connectionsError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation connections: ${connectionsError.message}`);
    }

    // Delete automation nodes
    const { error: nodesError } = await supabase
      .from('automation_nodes')
      .delete()
      .eq('automation_id', automationId);

    if (nodesError && nodesError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation nodes: ${nodesError.message}`);
    }

    // Finally delete the automation itself
    const { error } = await supabase
      .from('automations_v2')
      .delete()
      .eq('id', automationId);

    if (error) {
      throw new Error(`Failed to delete automation: ${error.message}`);
    }
  } catch (error) {
    console.error('Error deleting automation:', error);
    throw error;
  }
}

// ==================== NODE MANAGEMENT ====================

// Get all nodes for an automation
export async function getAutomationNodes(automationId: string): Promise<AutomationNode[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching automation nodes:', error);
    throw error;
  }
}

// Get trigger nodes for an automation (converted to TriggerNode format for frontend)
export async function getAutomationTriggers(automationId: string): Promise<TriggerNode[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'trigger')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertNodeToTrigger);
  } catch (error) {
    console.error('Error fetching automation triggers:', error);
    throw error;
  }
}

// Get action nodes for an automation (converted to ActionNode format for frontend)
export async function getAutomationActions(automationId: string): Promise<ActionNode[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'action')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertNodeToAction);
  } catch (error) {
    console.error('Error fetching automation actions:', error);
    throw error;
  }
}

// Create a new node
export async function createAutomationNode(request: CreateNodeRequest): Promise<AutomationNode> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .insert({
        automation_id: request.automation_id,
        node_type: request.node_type,
        node_subtype: request.node_subtype,
        name: request.name,
        position_x: request.position_x || 100,
        position_y: request.position_y || 100,
        configuration: request.configuration || {}
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation node:', error);
    throw error;
  }
}

// Update an existing node
export async function updateAutomationNode(nodeId: string, updates: Partial<AutomationNode>): Promise<AutomationNode> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .update(updates)
      .eq('id', nodeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating automation node:', error);
    throw error;
  }
}

// Delete a node
export async function deleteAutomationNode(nodeId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { error } = await supabase
      .from('automation_nodes')
      .delete()
      .eq('id', nodeId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting automation node:', error);
    throw error;
  }
}

// Save triggers (for frontend compatibility)
export async function saveAutomationTriggers(automationId: string, triggers: TriggerNode[]): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // Get existing trigger nodes
    const { data: existingNodes } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'trigger');

    const existingIds = new Set((existingNodes || []).map(n => n.id));
    const currentIds = new Set(triggers.map(t => t.id));

    // Delete nodes that are no longer present
    for (const node of existingNodes || []) {
      if (!currentIds.has(node.id)) {
        await deleteAutomationNode(node.id);
      }
    }

    // Update or create nodes
    for (const trigger of triggers) {
      const nodeData = convertTriggerToNode(trigger, automationId);
      
      if (existingIds.has(trigger.id)) {
        // Update existing node
        await updateAutomationNode(trigger.id, nodeData);
      } else {
        // Create new node
        await createAutomationNode({
          automation_id: automationId,
          node_type: 'trigger',
          node_subtype: trigger.type,
          name: trigger.name,
          position_x: trigger.position.x,
          position_y: trigger.position.y,
          configuration: trigger.config
        });
      }
    }
  } catch (error) {
    console.error('Error saving automation triggers:', error);
    throw error;
  }
}

// Save actions (for frontend compatibility)
export async function saveAutomationActions(automationId: string, actions: ActionNode[]): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // Get existing action nodes
    const { data: existingNodes } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'action');

    const existingIds = new Set((existingNodes || []).map(n => n.id));
    const currentIds = new Set(actions.map(a => a.id));

    // Delete nodes that are no longer present
    for (const node of existingNodes || []) {
      if (!currentIds.has(node.id)) {
        await deleteAutomationNode(node.id);
      }
    }

    // Update or create nodes
    for (const action of actions) {
      const nodeData = convertActionToNode(action, automationId);
      
      if (existingIds.has(action.id)) {
        // Update existing node
        await updateAutomationNode(action.id, nodeData);
      } else {
        // Create new node
        await createAutomationNode({
          automation_id: automationId,
          node_type: 'action',
          node_subtype: action.type,
          name: action.name,
          position_x: action.position.x,
          position_y: action.position.y,
          configuration: action.config
        });
      }
    }
  } catch (error) {
    console.error('Error saving automation actions:', error);
    throw error;
  }
}

// ==================== CONNECTION MANAGEMENT ====================

// Get all connections for an automation
export async function getAutomationConnections(automationId: string): Promise<WireConnection[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_connections')
      .select('*')
      .eq('automation_id', automationId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertConnectionToWire);
  } catch (error) {
    console.error('Error fetching automation connections:', error);
    throw error;
  }
}

// Create a new connection
export async function createAutomationConnection(request: CreateConnectionRequest): Promise<AutomationConnection> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_connections')
      .insert({
        automation_id: request.automation_id,
        source_node_id: request.source_node_id,
        target_node_id: request.target_node_id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation connection:', error);
    throw error;
  }
}

// Delete a connection
export async function deleteAutomationConnection(connectionId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { error } = await supabase
      .from('automation_connections')
      .delete()
      .eq('id', connectionId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting automation connection:', error);
    throw error;
  }
}

// Save wire connections (for frontend compatibility)
export async function saveWireConnections(automationId: string, connections: WireConnection[]): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // Get existing connections
    const { data: existingConnections } = await supabase
      .from('automation_connections')
      .select('*')
      .eq('automation_id', automationId);

    const existingIds = new Set((existingConnections || []).map(c => c.id));
    const currentIds = new Set(connections.map(c => c.id));

    // Delete connections that are no longer present
    for (const connection of existingConnections || []) {
      if (!currentIds.has(connection.id)) {
        await deleteAutomationConnection(connection.id);
      }
    }

    // Create new connections (connections don't need updates, just create/delete)
    for (const wireConnection of connections) {
      if (!existingIds.has(wireConnection.id)) {
        await createAutomationConnection({
          automation_id: automationId,
          source_node_id: wireConnection.fromNodeId,
          target_node_id: wireConnection.toNodeId
        });
      }
    }
  } catch (error) {
    console.error('Error saving wire connections:', error);
    throw error;
  }
}

// ==================== EXECUTION TRACKING ====================

// Create a new automation execution record
export async function createAutomationExecution(
  automationId: string, 
  triggerData: Record<string, any>
): Promise<AutomationExecution> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_executions')
      .insert({
        automation_id: automationId,
        trigger_data: triggerData,
        status: 'running'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation execution:', error);
    throw error;
  }
}

// Update execution status and completion
export async function updateAutomationExecution(
  executionId: string,
  updates: {
    status?: 'running' | 'completed' | 'failed' | 'cancelled';
    completed_at?: string;
    error_message?: string;
  }
): Promise<AutomationExecution> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const updateData: any = { ...updates };
    
    // Auto-set completed_at if status is final
    if (updates.status && ['completed', 'failed', 'cancelled'].includes(updates.status)) {
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('automation_executions')
      .update(updateData)
      .eq('id', executionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating automation execution:', error);
    throw error;
  }
}

// Get executions for an automation
export async function getAutomationExecutions(automationId: string): Promise<AutomationExecution[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_executions')
      .select('*')
      .eq('automation_id', automationId)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching automation executions:', error);
    throw error;
  }
}

// Get execution summary/statistics
export async function getExecutionSummary(automationId: string): Promise<{
  total_executions: number;
  successful_executions: number;
  failed_executions: number;
  average_duration_ms: number;
  last_execution?: AutomationExecution;
}> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data: executions, error } = await supabase
      .from('automation_executions')
      .select('*')
      .eq('automation_id', automationId)
      .order('started_at', { ascending: false });

    if (error) throw error;

    const total = executions?.length || 0;
    const successful = executions?.filter(e => e.status === 'completed').length || 0;
    const failed = executions?.filter(e => e.status === 'failed').length || 0;
    
    // Calculate average duration for completed executions
    const completedExecutions = executions?.filter(e => e.status === 'completed' && e.completed_at) || [];
    const totalDuration = completedExecutions.reduce((sum, exec) => {
      const start = new Date(exec.started_at).getTime();
      const end = new Date(exec.completed_at!).getTime();
      return sum + (end - start);
    }, 0);
    
    const averageDuration = completedExecutions.length > 0 ? totalDuration / completedExecutions.length : 0;

    return {
      total_executions: total,
      successful_executions: successful,
      failed_executions: failed,
      average_duration_ms: averageDuration,
      last_execution: executions?.[0]
    };
  } catch (error) {
    console.error('Error fetching execution summary:', error);
    throw error;
  }
}

// Get execution steps
export async function getExecutionSteps(executionId: string): Promise<AutomationExecutionStep[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // First try with a more explicit join
    const { data, error } = await supabase
      .from('automation_execution_steps')
      .select(`
        *,
        automation_nodes!inner(
          node_type,
          node_subtype,
          name,
          configuration
        )
      `)
      .eq('execution_id', executionId)
      .order('step_order', { ascending: true });

    if (error) {
      console.error('Error fetching execution steps with join:', error);
      
      // Fallback: Get steps and nodes separately
      const [stepsResult, nodesResult] = await Promise.all([
        supabase
          .from('automation_execution_steps')
          .select('*')
          .eq('execution_id', executionId)
          .order('step_order', { ascending: true }),
        supabase
          .from('automation_nodes')
          .select('*')
      ]);

      if (stepsResult.error) throw stepsResult.error;
      if (nodesResult.error) throw nodesResult.error;

      // Manually join the data
      const stepsWithNodes = (stepsResult.data || []).map(step => {
        const node = nodesResult.data?.find(n => n.id === step.node_id);
        return {
          ...step,
          automation_nodes: node ? {
            node_type: node.node_type,
            node_subtype: node.node_subtype,
            name: node.name,
            configuration: node.configuration
          } : null
        };
      });

      console.log('📋 Fallback execution steps with manual join:', stepsWithNodes);
      return stepsWithNodes.map(step => ({
        ...step,
        automation_node: step.automation_nodes
      }));
    }

    console.log('📋 Execution steps with join:', data);
    return (data || []).map(step => ({
      ...step,
      automation_node: step.automation_nodes
    }));
  } catch (error) {
    console.error('Error fetching execution steps:', error);
    throw error;
  }
}

// ==================== AUTOMATION EXECUTION ENGINE ====================

// Execute individual automation actions with result return
async function executeAutomationActionWithResult(
  executionId: string,
  node: AutomationNode,
  stepOrder: number,
  contextData: Record<string, any>
): Promise<Record<string, any> | null> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Create execution step record
  const { data: step, error: stepError } = await supabase
    .from('automation_execution_steps')
    .insert({
      execution_id: executionId,
      node_id: node.id,
      step_order: stepOrder,
      input_data: contextData,
      status: 'running',
      started_at: new Date().toISOString()
    })
    .select()
    .single();

  if (stepError) throw stepError;

  try {
    let result: Record<string, any> = {};

    // Execute action based on node type
    switch (node.node_subtype) {
      case 'sms':
        result = await executeSMSAction(node, contextData);
        break;
      
      case 'email':
        result = await executeEmailAction(node, contextData);
        break;
      
      case 'wait':
        result = await executeWaitAction(node, contextData);
        break;
      
      default:
        throw new Error(`Unknown action type: ${node.node_subtype}`);
    }

    // Update step as completed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        output_data: result
      })
      .eq('id', step.id);

    console.log(`✅ Executed action: ${node.name} (${node.node_subtype})`);
    return result; // Return the result for context enrichment
  } catch (error) {
    // Update step as failed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })
      .eq('id', step.id);

    console.error(`❌ Failed to execute action: ${node.name}`, error);
    throw error;
  }
}

// Legacy wrapper for backward compatibility
async function executeAutomationAction(
  executionId: string,
  node: AutomationNode,
  stepOrder: number,
  contextData: Record<string, any>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Create execution step record
  const { data: step, error: stepError } = await supabase
    .from('automation_execution_steps')
    .insert({
      execution_id: executionId,
      node_id: node.id,
      step_order: stepOrder,
      input_data: contextData,
      status: 'running',
      started_at: new Date().toISOString()
    })
    .select()
    .single();

  if (stepError) throw stepError;

  try {
    let result: Record<string, any> = {};

    // Execute action based on node type
    switch (node.node_subtype) {
      case 'sms':
        result = await executeSMSAction(node, contextData);
        break;
      
      case 'email':
        result = await executeEmailAction(node, contextData);
        break;
      
      case 'wait':
        result = await executeWaitAction(node, contextData);
        break;
      
      default:
        throw new Error(`Unknown action type: ${node.node_subtype}`);
    }

    // Update step as completed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        output_data: result
      })
      .eq('id', step.id);

    console.log(`✅ Executed action: ${node.name} (${node.node_subtype})`);
  } catch (error) {
    // Update step as failed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })
      .eq('id', step.id);

    console.error(`❌ Failed to execute action: ${node.name}`, error);
    throw error;
  }
}

// Helper function to format dates for SMS messages
function formatDateForSMS(dateString: string): string {
  try {
    // Handle various date formats
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date format for SMS:', dateString);
      return dateString; // Return original if parsing fails
    }
    
    // Format as "April 10, 2025"
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    };
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.warn('Error formatting date for SMS:', dateString, error);
    return dateString; // Return original if any error occurs
  }
}

// Helper function to format times for SMS messages
function formatTimeForSMS(timeString: string): string {
  try {
    // Handle various time formats (HH:MM, H:MM, etc.)
    if (!timeString) return timeString;
    
    // If it's already in AM/PM format, return as is
    if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
      return timeString;
    }
    
    // Parse time string (assume HH:MM format)
    const timeMatch = timeString.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!timeMatch) {
      console.warn('Invalid time format for SMS:', timeString);
      return timeString;
    }
    
    let hours = parseInt(timeMatch[1], 10);
    const minutes = timeMatch[2];
    
    // Convert to 12-hour format with AM/PM
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Handle midnight (0 hours = 12 AM)
    
    // Format as "9:00 AM" or "1:30 PM"
    return `${hours}:${minutes} ${ampm}`;
  } catch (error) {
    console.warn('Error formatting time for SMS:', timeString, error);
    return timeString; // Return original if any error occurs
  }
}

// Execute SMS action
async function executeSMSAction(
  node: AutomationNode,
  contextData: Record<string, any>
): Promise<Record<string, any>> {
  const config = node.configuration as SMSActionConfig;
  const bookingData = contextData.booking_data;
  
  console.log('📱 SMS Action Debug Info:');
  console.log('- Node config:', config);
  console.log('- Context data keys:', Object.keys(contextData));
  console.log('- Booking data structure:', JSON.stringify(bookingData, null, 2));
  console.log('- Customer data:', bookingData?.customer);
  console.log('- Schedule data:', bookingData?.schedule);
  console.log('- Business data:', bookingData?.business);
  console.log('- Business ID from context:', contextData.business_id);
  
  if (!config.message || !config.recipient) {
    throw new Error('SMS action missing required configuration');
  }

  // Process message with dynamic data
  let processedMessage = config.message;
  
  // Customer variables
  if (bookingData?.customer?.full_name || bookingData?.customer?.name || contextData.customer?.name) {
    const customerName = bookingData?.customer?.full_name || bookingData?.customer?.name || contextData.customer?.name;
    processedMessage = processedMessage.replace(/\{customer_name\}/g, customerName);
  }
  if (bookingData?.customer?.phone || contextData.customer?.phone) {
    processedMessage = processedMessage.replace(/\{customer_phone\}/g, bookingData?.customer?.phone || contextData.customer?.phone);
  }
  if (bookingData?.customer?.email || contextData.customer?.email) {
    processedMessage = processedMessage.replace(/\{customer_email\}/g, bookingData?.customer?.email || contextData.customer?.email);
  }
  
  // Booking variables
  if (bookingData?.booking_id || contextData.booking_id) {
    processedMessage = processedMessage.replace(/\{booking_number\}/g, bookingData?.booking_id || contextData.booking_id);
  }
  if (bookingData?.title || contextData.title) {
    processedMessage = processedMessage.replace(/\{booking_title\}/g, bookingData?.title || contextData.title);
  }
  if (bookingData?.grand_total || contextData.grand_total) {
    processedMessage = processedMessage.replace(/\{grand_total\}/g, bookingData?.grand_total || contextData.grand_total);
  }
  
  // Schedule variables with date formatting
  if (bookingData?.schedule?.start_date || contextData.schedule?.start_date) {
    const startDate = bookingData?.schedule?.start_date || contextData.schedule?.start_date;
    const formattedStartDate = formatDateForSMS(startDate);
    processedMessage = processedMessage.replace(/\{start_date\}/g, formattedStartDate);
  }
  if (bookingData?.schedule?.start_time || contextData.schedule?.start_time) {
    const startTime = bookingData?.schedule?.start_time || contextData.schedule?.start_time;
    const formattedStartTime = formatTimeForSMS(startTime);
    processedMessage = processedMessage.replace(/\{start_time\}/g, formattedStartTime);
  }
  if (bookingData?.schedule?.drop_off_date || contextData.schedule?.drop_off_date) {
    const dropOffDate = bookingData?.schedule?.drop_off_date || contextData.schedule?.drop_off_date;
    const formattedDropOffDate = formatDateForSMS(dropOffDate);
    processedMessage = processedMessage.replace(/\{drop_off_date\}/g, formattedDropOffDate);
  }
  if (bookingData?.schedule?.drop_off_time || contextData.schedule?.drop_off_time) {
    const dropOffTime = bookingData?.schedule?.drop_off_time || contextData.schedule?.drop_off_time;
    const formattedDropOffTime = formatTimeForSMS(dropOffTime);
    processedMessage = processedMessage.replace(/\{drop_off_time\}/g, formattedDropOffTime);
  }
  


  // Handle different recipient types
  if (config.recipient === 'resource_phone') {
    // Special handling for resource phone - send to all assigned resources
    const resources = bookingData?.resources || bookingData?.booking_resources || [];
    console.log('📱 SMS Action Debug - Resource Phone Processing:');
    console.log('📱 Raw booking data keys:', Object.keys(bookingData || {}));
    console.log('📱 Found resources in booking data:', resources);
    console.log('📱 Number of resources found:', resources ? resources.length : 0);
    
    if (!resources || resources.length === 0) {
      throw new Error('No resources assigned to this booking');
    }
    
    // Extract phone numbers from resources
    console.log('📱 Individual resource details:');
    resources.forEach((resource: any, index: number) => {
      console.log(`📱   Resource ${index + 1}:`, {
        id: resource?.id,
        name: resource?.name,
        phone: resource?.phone,
        role: resource?.role,
        nested_phone: resource?.resource?.phone
      });
    });
    
    const resourcePhones = resources
      .map((resource: any) => resource?.phone || resource?.resource?.phone)
      .filter((phone: string) => phone && phone.trim() !== '');
    
    console.log('📱 Extracted phone numbers:', resourcePhones);
    
    if (resourcePhones.length === 0) {
      throw new Error('No phone numbers found for assigned resources');
    }
    
    console.log('📱 SMS Details (Multiple Resources):');
    console.log('- To:', resourcePhones);
    console.log('- Message:', processedMessage);
    console.log('- Business ID:', contextData.business_id);
    
    // Send SMS to all resource phone numbers
    const smsResults = [];
    const failedSends = [];
    
    for (const phone of resourcePhones) {
      try {
        const result = await sendSMS({
          businessId: contextData.business_id,
          to: phone,
          message: processedMessage
        });
        
        if (result.success) {
          smsResults.push({
            recipient: phone,
            success: true,
            result: result
          });
          console.log(`📱 SMS sent successfully to resource: ${phone}`);
        } else {
          failedSends.push({
            recipient: phone,
            error: result.error || 'SMS sending failed'
          });
          console.error(`📱 SMS failed to resource: ${phone}`, result.error);
        }
      } catch (error) {
        failedSends.push({
          recipient: phone,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        console.error(`📱 SMS sending failed to resource: ${phone}`, error);
      }
    }
    
    // Return comprehensive result
    const allSuccessful = failedSends.length === 0;
    
    return {
      success: allSuccessful,
      message: processedMessage,
      sent_at: new Date().toISOString(),
      total_recipients: resourcePhones.length,
      successful_sends: smsResults.length,
      failed_sends: failedSends.length,
      results: smsResults,
      failures: failedSends.length > 0 ? failedSends : undefined
    };
  } else {
    // Handle single recipient cases
    let recipientPhone: string | undefined;
    
    switch (config.recipient) {
      case 'customer_phone':
        recipientPhone = bookingData?.customer?.phone || contextData.customer?.phone;
        if (!recipientPhone) throw new Error('Customer phone not available');
        break;
      case 'business_phone':
        recipientPhone = bookingData?.business?.phone || contextData.business?.phone;
        if (!recipientPhone) throw new Error('Business phone not available');
        break;
      case 'custom':
        recipientPhone = config.customPhone;
        if (!recipientPhone) throw new Error('Custom phone number not provided');
        break;
      default:
        throw new Error(`Unknown recipient type: ${config.recipient}`);
    }

    console.log('📱 SMS Details:');
    console.log('- To:', recipientPhone);
    console.log('- Message:', processedMessage);
    console.log('- Business ID:', contextData.business_id);

    // Send SMS using the existing messaging function
    try {
      const result = await sendSMS({
        businessId: contextData.business_id,
        to: recipientPhone,
        message: processedMessage
      });

      if (!result.success) {
        throw new Error(result.error || 'SMS sending failed');
      }

      console.log('📱 SMS sent successfully via messaging.ts');

      return {
        success: true,
        recipient: recipientPhone,
        message: processedMessage,
        sent_at: new Date().toISOString(),
        sms_result: result
      };
    } catch (error) {
      console.error('📱 SMS sending failed:', error);
      throw new Error(`Failed to send SMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Execute email action (placeholder for future implementation)
async function executeEmailAction(
  node: AutomationNode,
  contextData: Record<string, any>
): Promise<Record<string, any>> {
  // TODO: Implement email action execution
  console.log('Email action execution not yet implemented');
  return {
    success: true,
    message: 'Email action placeholder - not yet implemented'
  };
}

// Execute wait action with support for both duration and reminder modes
async function executeWaitAction(
  node: AutomationNode,
  contextData: Record<string, any>
): Promise<Record<string, any>> {
  console.log('🕒 Executing wait action:', node.id);
  
  try {
    const config = node.configuration || {};
    const waitType = config.wait_type || 'duration';
    
    if (waitType === 'reminder') {
      // Reminder mode - calculate wait time based on booking schedule
      const reminderHoursBefore = config.reminder_hours_before || 24;
      const reminderReference = config.reminder_reference || 'drop_off_datetime';
      
      console.log(`🔔 Reminder mode: ${reminderHoursBefore} hours before ${reminderReference}`);
      
      // Extract booking data from context
      const bookingData = contextData.booking_data || contextData;
      const schedule = bookingData?.schedule;
      
      if (!schedule) {
        console.error('❌ No schedule data found for reminder calculation');
        return {
          success: false,
          message: 'No schedule data available for reminder calculation'
        };
      }
      
      // Get reference date and time
      let referenceDate: string;
      let referenceTime: string;
      
      if (reminderReference === 'drop_off_datetime') {
        referenceDate = schedule.drop_off_date || schedule.start_date;
        referenceTime = schedule.drop_off_time || schedule.start_time || '12:00';
      } else {
        referenceDate = schedule.start_date || schedule.drop_off_date;
        referenceTime = schedule.start_time || schedule.drop_off_time || '12:00';
      }
      
      if (!referenceDate) {
        console.error('❌ No reference date found for reminder calculation');
        return {
          success: false,
          message: 'No reference date available for reminder calculation'
        };
      }
      
      // Calculate target reminder time
      try {
        const appointmentDateTime = new Date(`${referenceDate}T${referenceTime}`);
        const reminderDateTime = new Date(appointmentDateTime.getTime() - (reminderHoursBefore * 60 * 60 * 1000));
        const currentTime = new Date();
        
        console.log(`📅 Appointment time: ${appointmentDateTime.toISOString()}`);
        console.log(`⏰ Reminder time: ${reminderDateTime.toISOString()}`);
        console.log(`🕐 Current time: ${currentTime.toISOString()}`);
        
        // Calculate wait time in milliseconds
        const waitTimeMs = reminderDateTime.getTime() - currentTime.getTime();
        
        if (waitTimeMs <= 0) {
          console.log('⚡ Reminder time has already passed, executing immediately');
          return {
            success: true,
            message: 'Reminder time has passed, action executed immediately'
          };
        }
        
        console.log(`⏳ Waiting ${Math.round(waitTimeMs / 1000 / 60)} minutes until reminder time`);
        
        // Perform the scheduled wait
        await new Promise(resolve => setTimeout(resolve, waitTimeMs));
        
        console.log('✅ Reminder wait completed, continuing automation');
        return {
          success: true,
          message: `Reminder executed ${reminderHoursBefore} hours before ${reminderReference}`,
          reminder_scheduled_for: reminderDateTime.toISOString(),
          appointment_time: appointmentDateTime.toISOString()
        };
        
      } catch (error) {
        console.error('❌ Error calculating reminder time:', error);
        return {
          success: false,
          message: 'Error calculating reminder schedule'
        };
      }
      
    } else {
      // Duration mode - standard wait for fixed duration
      const durationMinutes = config.duration_minutes || config.duration || 30;
      const waitTimeMs = durationMinutes * 60 * 1000;
      
      console.log(`⏱️ Duration mode: waiting ${durationMinutes} minutes (${waitTimeMs}ms)`);
      
      // Perform the wait
      await new Promise(resolve => setTimeout(resolve, waitTimeMs));
      
      console.log('✅ Duration wait completed, continuing automation');
      return {
        success: true,
        message: `Waited ${durationMinutes} minutes`,
        duration_minutes: durationMinutes
      };
    }
    
  } catch (error) {
    console.error('❌ Wait action failed:', error);
    return {
      success: false,
      message: `Wait action failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

// Main workflow execution function
async function executeAutomationWorkflow(
  automationId: string,
  executionId: string,
  contextData: Record<string, any>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    console.log(`🚀 Starting workflow execution for automation: ${automationId}`);

    // Get automation nodes and connections
    const [nodesData, connectionsData] = await Promise.all([
      supabase
        .from('automation_nodes')
        .select('*')
        .eq('automation_id', automationId)
        .order('created_at', { ascending: true }),
      supabase
        .from('automation_connections')
        .select('*')
        .eq('automation_id', automationId)
    ]);

    if (nodesData.error) throw nodesData.error;
    if (connectionsData.error) throw connectionsData.error;

    const nodes = nodesData.data || [];
    const connections = connectionsData.data || [];

    // Build execution order using topological sort
    const executionOrder = buildExecutionOrder(nodes, connections);
    
    console.log(`📋 Execution order: ${executionOrder.length} steps`);

    // Execute each node in order with data pipeline
    let stepOrder = 1;
    let enrichedContextData = { ...contextData }; // Start with original context
    
    for (const node of executionOrder) {
      if (node.node_type === 'action') {
        console.log(`⚡ Executing step ${stepOrder}: ${node.name} (${node.node_subtype})`);
        console.log(`📋 Context data keys available:`, Object.keys(enrichedContextData));
        
        // Execute action and get result
        const actionResult = await executeAutomationActionWithResult(executionId, node, stepOrder, enrichedContextData);
        
        // Add action result to context for next actions
        if (actionResult) {
          enrichedContextData = {
            ...enrichedContextData,
            [`action_${stepOrder}_result`]: actionResult,
            [`${node.node_subtype}_result`]: actionResult // Also add by action type
          };
          console.log(`📈 Added action result to context:`, Object.keys(actionResult));
        }
        
        stepOrder++;
      }
    }

    // Update execution as completed
    await updateAutomationExecution(executionId, {
      status: 'completed',
      completed_at: new Date().toISOString()
    });

    console.log(`✅ Workflow execution completed for automation: ${automationId}`);
  } catch (error) {
    console.error(`❌ Workflow execution failed for automation: ${automationId}`, error);
    
    // Update execution as failed
    await updateAutomationExecution(executionId, {
      status: 'failed',
      completed_at: new Date().toISOString(),
      error_message: error instanceof Error ? error.message : 'Unknown error'
    });
    
    throw error;
  }
}

// Build execution order using topological sort
function buildExecutionOrder(
  nodes: AutomationNode[],
  connections: AutomationConnection[]
): AutomationNode[] {
  // Start with trigger nodes (no incoming connections)
  const triggerNodes = nodes.filter(node => 
    node.node_type === 'trigger' && 
    !connections.some(conn => conn.target_node_id === node.id)
  );

  // Build execution order starting from triggers
  const executionOrder: AutomationNode[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();

  function visit(nodeId: string) {
    if (visiting.has(nodeId)) {
      throw new Error('Circular dependency detected in automation workflow');
    }
    if (visited.has(nodeId)) return;

    visiting.add(nodeId);
    
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    // Visit all nodes that this node depends on (incoming connections)
    const incomingConnections = connections.filter(conn => conn.target_node_id === nodeId);
    for (const connection of incomingConnections) {
      visit(connection.source_node_id);
    }

    visiting.delete(nodeId);
    visited.add(nodeId);
    executionOrder.push(node);
  }

  // Start with trigger nodes
  for (const trigger of triggerNodes) {
    visit(trigger.id);
  }

  // Visit any remaining nodes
  for (const node of nodes) {
    if (!visited.has(node.id)) {
      visit(node.id);
    }
  }

  return executionOrder;
}

// Trigger automation execution for booking events using QStash
export async function triggerBookingAutomations(
  businessId: string,
  eventType: 'created' | 'in_progress' | 'completed' | 'canceled' | 'deleted' | 'partially_paid' | 'paid',
  bookingData: Record<string, any>
): Promise<void> {
  if (!supabase) return;

  try {
    console.log(`🎯 Triggering QStash-based booking automations for event: ${eventType}`);

    // Find live automations with booking triggers that match this event type
    const { data: automations, error: automationsError } = await supabase
      .from('automations_v2')
      .select(`
        id,
        name,
        automation_nodes!inner(
          id,
          node_type,
          node_subtype,
          configuration
        )
      `)
      .eq('business_id', businessId)
      .eq('is_live', true)
      .eq('automation_nodes.node_type', 'trigger')
      .eq('automation_nodes.node_subtype', 'booking');

    if (automationsError) throw automationsError;

    console.log(`📋 Found ${automations?.length || 0} potential automations`);

    // Filter automations by event type and trigger them
    const matchingAutomations = [];
    for (const automation of automations || []) {
      const bookingTriggers = automation.automation_nodes?.filter(
        (node: any) => 
          node.node_type === 'trigger' && 
          node.node_subtype === 'booking' &&
          node.configuration?.eventType === eventType
      ) || [];

      if (bookingTriggers.length > 0) {
        matchingAutomations.push(automation);
      }
    }

    console.log(`✅ Found ${matchingAutomations.length} matching automations for event: ${eventType}`);

    // Start QStash-based execution for each matching automation
    for (const automation of matchingAutomations) {
      try {
        console.log(`🚀 Starting QStash automation: ${automation.name}`);

        // Create execution record
        const execution = await createAutomationExecution(automation.id, {
          booking_data: bookingData,
          trigger_type: 'booking',
          event_type: eventType,
          triggered_at: new Date().toISOString()
        });

        console.log(`📝 Created execution record: ${execution.id}`);

        // Start the QStash-based workflow
        await startQStashAutomation(automation.id, execution.id, {
          booking_data: bookingData,
          trigger_type: 'booking',
          event_type: eventType,
          business_id: businessId
        });

        console.log(`✅ Initiated QStash workflow for: ${automation.name}`);
      } catch (error) {
        console.error(`❌ Failed to start QStash automation: ${automation.name}`, error);
      }
    }
  } catch (error) {
    console.error('❌ Error triggering QStash automations:', error);
    // Don't throw here to avoid breaking the booking creation flow
  }
}

// Start QStash-based automation execution
async function startQStashAutomation(
  automationId: string,
  executionId: string,
  contextData: Record<string, any>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    // Create a unique job ID
    const jobId = crypto.randomUUID();

    console.log(`🎯 Starting QStash automation - Job ID: ${jobId}`);

    // Call the execute-automation-step edge function to start the workflow
    const { data, error } = await supabase.functions.invoke('execute-automation-step', {
      body: {
        jobId,
        automationId,
        executionId,
        currentStep: 1, // Start with step 1
        contextData
      }
    });

    if (error) {
      throw new Error(`Failed to start QStash automation: ${error.message}`);
    }

    console.log(`✅ QStash automation started successfully:`, data);

    // Create initial job tracking record
    await supabase
      .from('automation_jobs')
      .insert({
        id: jobId,
        qstash_message_id: data?.messageId || null,
        automation_id: automationId,
        execution_id: executionId,
        scheduled_for: new Date().toISOString(),
        current_step: 1,
        status: 'scheduled',
        context_data: contextData
      });

    console.log(`📝 Created job tracking record: ${jobId}`);

  } catch (error) {
    console.error('❌ Failed to start QStash automation:', error);
    
    // Update execution record as failed
    await updateAutomationExecution(executionId, {
      status: 'failed',
      error_message: error instanceof Error ? error.message : 'Failed to start QStash automation'
    });
    
    throw error;
  }
} 